import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";

const getErrorMessage = (
	error: Error,
): { title: string; description?: string } => {
	if (error instanceof ApiError) {
		const data = error.data as { message?: string } | undefined;
		const apiMessage = data?.message;
		return {
			title: `API Error: ${error.status} ${error.statusText}`,
			description: apiMessage,
		};
	}
	return { title: error.message };
};

export function getContext() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				// Don't retry on 401/403 - these are auth failures, not transient errors
				retry: (failureCount, error) => {
					if (error instanceof ApiError && [401, 403].includes(error.status)) {
						return false;
					}
					return failureCount < 3;
				},
				// Prevent refetching when window regains focus during auth redirect
				refetchOnWindowFocus: false,
			},
		},
		queryCache: new QueryCache({
			onError: (error, query) => {
				// Skip error toasts for auth-related errors (401/403) - these are handled by redirect
				if (error instanceof ApiError && [401, 403].includes(error.status)) {
					return;
				}
				if (query.meta?.skipGlobalErrorHandler) return;
				const { title, description } = getErrorMessage(error);
				toast.error(title, { description });
			},
		}),
		mutationCache: new MutationCache({
			onError: (error, _variables, _context, mutation) => {
				if (mutation.meta?.skipGlobalErrorHandler) return;
				const { title, description } = getErrorMessage(error);
				toast.error(title, { description });
			},
		}),
	});

	return {
		queryClient,
	};
}

export function Provider({
	children,
	queryClient,
}: {
	children: React.ReactNode;
	queryClient: QueryClient;
}) {
	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
